
/*
    🔥 FAVICONS Y ESCRITURA EN EL <head>

    Escribe en el <head> lo que hasta ahora estaba a mano en public/index.html: título,
    descripción, keywords, autor, color de tema, og:, twitter: y favicons.
    Todo sale de _Company.js.

    ⚠️ POR QUÉ ESTO EXISTE Y NO SE IMPORTA APP_COMPANY DENTRO DE AppSeo

    AppSeo vive en _CORE/Templates/, y esa carpeta tiene la regla de no importar
    configuración para poder copiarse a otro proyecto tal cual. AppSeo resuelve el
    nombre del sitio leyendo document.title:

        const SITE = siteName ?? document.title.split('|').pop()?.trim()

    Fijando acá el título al arrancar, esa lectura pasa a ser correcta por construcción
    y Templates no se toca. Cero acoplamiento.

    ⚠️ EL PARPADEO ES INEVITABLE

    index.html es un archivo estático que el navegador pinta ANTES de que corra un solo
    byte de JavaScript. Create React App no lo templatea con valores de tus módulos.
    O sea: se ve lo que dice index.html unos 100-300 ms y recién después entra esto.

    Por eso index.html conserva los mismos valores: mientras coincidan, el parpadeo no
    se nota. Si cambiás el nombre de la empresa, actualizá también el <title> de
    index.html (está comentado allá).

    ⚠️ LOS PREVIEWS DE WHATSAPP / LINKEDIN NO LEEN ESTO

    Esos scrapers no ejecutan JavaScript: leen el HTML crudo. Las og: que escribe este
    archivo (y las que escribe AppSeo) no las ven nunca. Para que el preview al compartir
    un link sea correcto, las etiquetas og: de public/index.html tienen que estar bien a
    mano. La alternativa real es SSR o prerender.
*/

import { APP_COMPANY } from "./_Company";
import { GET_ACTIVE_LOGO } from "./_GetLogo";


// ---------------------------------------- 🔥 ÍCONOS
/*
    El favicon sale de public/_Imgs/_Favicon/FAVICON.png. Cambiás ese archivo y cambia el
    favicon: no hay que tocar código.

    ⚠️ AHORA VIVE EN public/, NO SE IMPORTA COMO MÓDULO.
       La ruta se arma en AppCompanyConfig > COMPANY_IMGS. Como no pasa por webpack, si
       el archivo no está el build igual pasa y el navegador se queda sin ícono.

    ⚠️ public/favicon.ico SIGUE SIENDO NECESARIO.
       El <link rel="icon"> de index.html manda los primeros ~200 ms, antes de que corra
       este JS. Si lo borrás, vas a ver el ícono por defecto del navegador en ese rato.

    ⚠️ USE_CALENDAR engancha el favicon al mismo calendario que el logo (_GetLogo):
       el 8 de diciembre pasaría al navideño solo.

       Está APAGADO porque necesita íconos CUADRADOS y los LS_BRAND_* son logotipos
       horizontales de 749x345: metidos en un favicon de 16x16 quedan una mancha ilegible.

       Para prenderlo: exportá los isotipos festivos cuadrados a public/_Imgs/_Favicon/,
       agregalos a COMPANY_IMGS, mapealos en BY_LOGO_ID y poné USE_CALENDAR true.
*/
const PUBLIC_URL = process.env.PUBLIC_URL ?? "";

export const COMPANY_ICONS = {

    ICON: APP_COMPANY.COMPANY_IMGS.FAVICON,
    APPLE_TOUCH: APP_COMPANY.COMPANY_IMGS.FAVICON_APPLE,

    USE_CALENDAR: false,

    // Los id salen de _GetLogo.js: navidad, bandera, independencia, etc.
    BY_LOGO_ID: {
        // navidad: `${APP_COMPANY.COMPANY_FAVICON_PATH}/FAVICON_NAVIDAD.png`,
        // bandera: `${APP_COMPANY.COMPANY_FAVICON_PATH}/FAVICON_BANDERA.png`,
    },
};


// ---------------------------------------- 🔥 ESCRITURA EN EL <head>
const MARK = "data-ls-identity";

/*
    ⚠️ Si la etiqueta ya existe en index.html, se REUSA en vez de crear una nueva.
       Duplicar <meta name="description"> hace que Google elija cualquiera de las dos.
*/
const onSetMeta = (attr, key, content) => {

    if (!content) return;

    let el = document.head.querySelector(`meta[${attr}="${key}"]`);

    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        el.setAttribute(MARK, "");
        document.head.appendChild(el);
    }

    el.setAttribute("content", content);
};

const onSetIcon = (rel, href) => {

    if (!href) return;

    let el = document.head.querySelector(`link[rel="${rel}"]`);

    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        el.setAttribute(MARK, "");
        document.head.appendChild(el);
    }

    el.setAttribute("href", href);
};


// ---------------------------------------- 🔥 FAVICON ACTIVO
export const GET_COMPANY_FAVICON = (date = new Date()) => {

    if (!COMPANY_ICONS.USE_CALENDAR) return COMPANY_ICONS.ICON;

    const LOGO = GET_ACTIVE_LOGO(date);

    return COMPANY_ICONS.BY_LOGO_ID?.[LOGO.id] ?? COMPANY_ICONS.ICON;
};


/*
    ---------------------------------------- 🔥 APLICAR

    Se llama UNA vez, en index.js, ANTES del render. Tiene que correr antes que cualquier
    AppSeo: si corriera después, pisaría el título que la vista acaba de poner.
*/
export const APPLY_COMPANY_IDENTITY = () => {

    if (typeof document === "undefined") return;

    // -------------------- 🎯 IDIOMA
    document.documentElement.setAttribute("lang", APP_COMPANY.COMPANY_LANG);

    document.title = APP_COMPANY.COMPANY_NAME;

    // -------------------- 🎯 BÁSICOS
    onSetMeta("name", "description", APP_COMPANY.COMPANY_DESCRIPTION);
    onSetMeta("name", "keywords", APP_COMPANY.COMPANY_KEYWORDS);
    onSetMeta("name", "author", APP_COMPANY.COMPANY_NAME);
    onSetMeta("name", "theme-color", APP_COMPANY.COMPANY_THEME_COLOR);
    onSetMeta("name", "application-name", APP_COMPANY.COMPANY_NAME);
    onSetMeta("name", "apple-mobile-web-app-title", APP_COMPANY.COMPANY_NAME);

    // -------------------- 🎯 REDES (defaults: cada vista los pisa)
    onSetMeta("property", "og:site_name", APP_COMPANY.COMPANY_NAME);
    onSetMeta("property", "og:locale", APP_COMPANY.COMPANY_LOCALE);

    /*
        ⚠️ Estos son el PISO, no la verdad final. AppSeo los sobrescribe con el título y
           la descripción de cada vista. Están acá para que una ruta sin AppSeo igual
           comparta algo coherente en vez del texto de otra pantalla.
    */
    onSetMeta("property", "og:type", "website");
    onSetMeta("property", "og:url", APP_COMPANY.COMPANY_WEBSITE);
    onSetMeta("property", "og:title", APP_COMPANY.COMPANY_OG_TITLE);
    onSetMeta("property", "og:description", APP_COMPANY.COMPANY_OG_DESCRIPTION);

    onSetMeta("name", "twitter:card", "summary_large_image");
    onSetMeta("name", "twitter:title", APP_COMPANY.COMPANY_TWITTER_TITLE);
    onSetMeta("name", "twitter:description", APP_COMPANY.COMPANY_TWITTER_DESCRIPTION);

    /*
        ⚠️ og:image tiene que ser ABSOLUTA. Con ruta relativa, la mitad de los
           previsualizadores no la resuelve y muestra el link pelado.
    */
    if (APP_COMPANY.COMPANY_OG_IMAGE) {
        const IS_ABSOLUTE = /^https?:\/\//i.test(APP_COMPANY.COMPANY_OG_IMAGE);
        const OG_IMAGE = IS_ABSOLUTE
            ? APP_COMPANY.COMPANY_OG_IMAGE
            : `${window.location.origin}${PUBLIC_URL}${APP_COMPANY.COMPANY_OG_IMAGE}`;

        onSetMeta("property", "og:image", OG_IMAGE);
        onSetMeta("name", "twitter:image", OG_IMAGE);
    }

    // -------------------- 🎯 ÍCONOS
    onSetIcon("icon", GET_COMPANY_FAVICON());
    onSetIcon("apple-touch-icon", COMPANY_ICONS.APPLE_TOUCH);
};